import { Metamodel } from '@ardoq/api-types';
import { connect } from '@ardoq/rxbeach';
import { MetamodelContext } from 'metamodel/types';
import { WorkspaceNavigationPopover } from './workspaceNavigationPopover/WorkspaceNavigationPopover';
import { trackClickContextTitle } from 'metamodel/tracking';
import { handleNavigation, handleNavigationToInventory } from 'metamodel/utils';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { Title, TitleButton } from './atoms';
import { getTrackingNavigationContext } from './utils';
import { type I18nLocale } from '@ardoq/i18n';
import { type ReactNode } from 'react';
import {
  ComponentWithDropdown,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { getWorkspaceNavigationOptions } from 'components/AssetsBrowser2024/workspaceNavigationOptions';

type WorkspaceNavigationTitleProps = {
  metamodel: Metamodel;
  context: MetamodelContext;
  children?: ReactNode;
  i18nLocale: I18nLocale;
  isViewpointMode: boolean;
};

const WorkspaceNavigationTitleView = ({
  metamodel,
  context,
  children,
  i18nLocale,
  isViewpointMode,
}: WorkspaceNavigationTitleProps) => {
  if (context.settings.isolatedNavigation) {
    return (
      <div>
        <Title>{children}</Title>
      </div>
    );
  }

  const openInWorkspace = () =>
    handleNavigation(metamodel, context, isViewpointMode);
  const openInInventory = () => handleNavigationToInventory(metamodel, context);
  const navigationOptions = getWorkspaceNavigationOptions({
    i18nLocale,
    onOpenInInventory: openInInventory,
    onOpenInWorkspace: openInWorkspace,
  });

  const trackTitleClick = () => {
    trackClickContextTitle(getTrackingNavigationContext(context));
  };

  const title =
    navigationOptions.length <= 1 ? (
      <Title>
        <TitleButton
          onClick={() => {
            trackTitleClick();
            (navigationOptions[0]?.onClick ?? openInWorkspace)();
          }}
        >
          {children}
        </TitleButton>
      </Title>
    ) : (
      <ComponentWithDropdown
        options={navigationOptions.map(option => ({
          type: DropdownOptionType.BUTTON_OPTION,
          ...option,
        }))}
        Component={({ ref, onMenuOpen, isMenuOpened }) => (
          <Title>
            <TitleButton
              ref={ref}
              aria-haspopup="menu"
              aria-expanded={isMenuOpened}
              onClick={() => {
                trackTitleClick();
                onMenuOpen();
              }}
            >
              {children}
            </TitleButton>
          </Title>
        )}
      />
    );

  return (
    <div>
      <WorkspaceNavigationPopover
        metamodel={metamodel}
        context={context}
        i18nLocale={i18nLocale}
      >
        {title}
      </WorkspaceNavigationPopover>
    </div>
  );
};

const WorkspaceNavigationTitle = connect(
  WorkspaceNavigationTitleView,
  isViewpointMode$
);

export { WorkspaceNavigationTitle };
